import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Edit2, ImagePlus, X, MapPin, Globe2, ShieldCheck, Save, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';

const RELATIONSHIP_INTENTS = [
  'Serious relationship',
  'Marriage-minded',
  'Friendship leading to relationship',
  'Business & Event Buddies',
];

export default function Profile() {
  const { user, setUser, refreshUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);

  async function handleGenerateBio() {
    setAiBusy(true);
    try {
      const { suggestion, fallback } = await api('/ai/bio-suggestion', { method: 'POST', body: JSON.stringify({}) });
      if (form?.bio?.trim() && !window.confirm('Replace your current bio with the AI suggestion?')) return;
      setForm((f) => ({ ...f, bio: suggestion }));
      if (fallback) toast.message('AI was busy — used a quick template you can edit.');
      else toast.success('Bio generated! Tweak it to make it yours.');
    } catch (err) {
      toast.error(err.message || 'Could not generate bio');
    } finally {
      setAiBusy(false);
    }
  }

  useEffect(() => {
    if (user && !form) {
      setForm({
        name: user.name || '',
        age: user.age || '',
        bio: user.bio || '',
        city: user.city || '',
        country_of_origin: user.country_of_origin || '',
        tribe: user.tribe || '',
        languages: (user.languages || []).join(', '),
        religion: user.religion || '',
        relationship_intents: user.relationship_intents || (user.relationship_intent ? [user.relationship_intent] : []),
      });
    }
  }, [user, form]);

  if (!user || !form) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-[#C8553D]" />
      </div>
    );
  }

  function update(k, v) { setForm({ ...form, [k]: v }); }

  function toggleIntent(intent) {
    setForm((f) => ({
      ...f,
      relationship_intents: f.relationship_intents.includes(intent)
        ? f.relationship_intents.filter((x) => x !== intent)
        : [...f.relationship_intents, intent],
    }));
  }

  async function handleSave() {
    if (form.bio && form.bio.trim().length < 20) {
      toast.error('Bio must be at least 20 characters');
      return;
    }
    setSaving(true);
    try {
      const data = await api('/users/me', {
        method: 'PUT',
        body: JSON.stringify({
          name: form.name,
          age: parseInt(form.age) || user.age,
          bio: form.bio,
          city: form.city,
          country_of_origin: form.country_of_origin,
          tribe: form.tribe,
          languages: form.languages.split(',').map((s) => s.trim()).filter(Boolean),
          religion: form.religion,
          relationship_intents: form.relationship_intents,
        }),
      });
      setUser(data.user);
      toast.success('Profile updated');
      setEditing(false);
    } catch (err) {
      toast.error(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function handleAddPhoto(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error('Max 5MB');
    const dataUrl = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(file);
    });
    try {
      await api('/users/photos', { method: 'POST', body: JSON.stringify({ photo: dataUrl }) });
      toast.success('Photo added');
      await refreshUser();
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    }
    e.target.value = '';
  }

  async function handleRemovePhoto(idx) {
    if (user.photos.length <= 2) return toast.error('Minimum 2 photos required');
    try {
      await api(`/users/photos/${idx}`, { method: 'DELETE' });
      toast.success('Photo removed');
      await refreshUser();
    } catch (err) {
      toast.error(err.message || 'Failed to remove');
    }
  }

  const fieldCls = 'w-full bg-white border border-[#F4F1DE] rounded-lg px-3 py-2 outline-none focus:border-[#E07A5F] text-[#2B2D42]';
  const labelCls = 'block text-xs font-bold uppercase tracking-wider text-[#6D6875] mb-1';

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6" data-testid="profile-page">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-3xl font-serif font-bold text-[#2B2D42]" style={{ fontFamily: 'Playfair Display, serif' }}>
          Your Profile
        </h1>
        {!editing ? (
          <button
            onClick={() => setEditing(true)}
            data-testid="profile-edit-button"
            className="flex items-center gap-2 bg-[#C8553D] hover:bg-[#9E3A26] text-white font-bold px-4 py-2 rounded-full text-sm"
          >
            <Edit2 className="w-4 h-4" /> Edit
          </button>
        ) : (
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="profile-save-button"
            className="flex items-center gap-2 bg-[#2A9D8F] hover:bg-[#21807a] disabled:opacity-60 text-white font-bold px-4 py-2 rounded-full text-sm"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </button>
        )}
      </div>

      {/* Photos */}
      <section className="bg-white border border-[#F4F1DE] rounded-2xl p-4 mb-5">
        <h2 className="text-sm font-bold uppercase tracking-wider text-[#6D6875] mb-3">Photos ({user.photos?.length || 0}/6)</h2>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="profile-photos-grid">
          {(user.photos || []).map((src, i) => (
            <div key={i} className="relative aspect-square rounded-lg overflow-hidden bg-[#FFE5DD] group" data-testid={`profile-photo-${i}`}>
              <img src={src} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
              {i === 0 && (
                <div className="absolute top-1 left-1 px-1.5 py-0.5 bg-[#C8553D] rounded text-white text-[9px] font-bold uppercase">Main</div>
              )}
              <button
                onClick={() => handleRemovePhoto(i)}
                data-testid={`profile-remove-photo-${i}`}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          {(user.photos?.length || 0) < 6 && (
            <label data-testid="profile-add-photo" className="aspect-square rounded-lg border-2 border-dashed border-[#E07A5F] bg-[#FFE5DD]/30 flex flex-col items-center justify-center cursor-pointer hover:bg-[#FFE5DD]/60 transition-colors">
              <ImagePlus className="w-6 h-6 text-[#C8553D] mb-1" />
              <span className="text-xs font-bold text-[#C8553D]">Add</span>
              <input type="file" accept="image/*" className="hidden" onChange={handleAddPhoto} />
            </label>
          )}
        </div>
      </section>

      {/* Identity */}
      <section className="bg-white border border-[#F4F1DE] rounded-2xl p-5 mb-5 space-y-4">
        <div>
          <p className={labelCls}>Name</p>
          {editing ? (
            <input data-testid="profile-name-input" className={fieldCls} value={form.name} onChange={(e) => update('name', e.target.value)} />
          ) : (
            <p className="text-lg font-bold text-[#2B2D42]" data-testid="profile-name">{user.name}, {user.age}</p>
          )}
        </div>

        {editing && (
          <div>
            <p className={labelCls}>Age</p>
            <input data-testid="profile-age-input" type="number" min="18" max="99" className={fieldCls} value={form.age} onChange={(e) => update('age', e.target.value)} />
          </div>
        )}

        <div>
          <div className="flex items-center justify-between gap-2">
            <p className={labelCls}>Bio</p>
            {editing && (
              <button
                type="button"
                onClick={handleGenerateBio}
                disabled={aiBusy}
                data-testid="profile-bio-ai-button"
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold text-white bg-gradient-to-r from-[#E07A5F] to-[#C8553D] hover:opacity-90 disabled:opacity-60"
              >
                {aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                {aiBusy ? 'Writing…' : 'Generate with AI'}
              </button>
            )}
          </div>
          {editing ? (
            <>
              <textarea data-testid="profile-bio-input" rows={5} className={fieldCls} value={form.bio} onChange={(e) => update('bio', e.target.value)} maxLength={2000} />
              <div className="text-xs text-right text-[#B5B5B5] mt-1">{(form.bio || '').length}/2000</div>
            </>
          ) : (
            <p className="text-sm text-[#6D6875] whitespace-pre-wrap" data-testid="profile-bio">{user.bio || 'No bio yet.'}</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className={labelCls}>City</p>
            {editing ? (
              <input data-testid="profile-city-input" className={fieldCls} value={form.city} onChange={(e) => update('city', e.target.value)} />
            ) : (
              <p className="text-sm font-semibold text-[#2B2D42] flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-[#B5B5B5]" />{user.city || '—'}</p>
            )}
          </div>
          <div>
            <p className={labelCls}>Country of origin</p>
            {editing ? (
              <input data-testid="profile-country-input" className={fieldCls} value={form.country_of_origin} onChange={(e) => update('country_of_origin', e.target.value)} />
            ) : (
              <p className="text-sm font-semibold text-[#2B2D42] flex items-center gap-1"><Globe2 className="w-3.5 h-3.5 text-[#B5B5B5]" />{user.country_of_origin || '—'}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className={labelCls}>Tribe</p>
            {editing ? (
              <input data-testid="profile-tribe-input" className={fieldCls} value={form.tribe} onChange={(e) => update('tribe', e.target.value)} />
            ) : (
              <p className="text-sm font-semibold text-[#2B2D42]">{user.tribe || '—'}</p>
            )}
          </div>
          <div>
            <p className={labelCls}>Religion</p>
            {editing ? (
              <input data-testid="profile-religion-input" className={fieldCls} value={form.religion} onChange={(e) => update('religion', e.target.value)} />
            ) : (
              <p className="text-sm font-semibold text-[#2B2D42]">{user.religion || '—'}</p>
            )}
          </div>
        </div>

        <div>
          <p className={labelCls}>Languages</p>
          {editing ? (
            <input data-testid="profile-languages-input" className={fieldCls} value={form.languages} onChange={(e) => update('languages', e.target.value)} placeholder="English, Yoruba, French" />
          ) : (
            <p className="text-sm font-semibold text-[#2B2D42]">{(user.languages || []).join(', ') || '—'}</p>
          )}
        </div>

        <div>
          <p className={labelCls}>Looking for</p>
          {editing ? (
            <div className="grid grid-cols-2 gap-2">
              {RELATIONSHIP_INTENTS.map((r) => {
                const active = form.relationship_intents.includes(r);
                return (
                  <button
                    type="button"
                    key={r}
                    onClick={() => toggleIntent(r)}
                    data-testid={`profile-intent-${r.replace(/[^a-z]+/gi, '-').toLowerCase()}`}
                    className={`text-left px-3 py-2 rounded-lg border text-xs font-semibold transition-all ${
                      active ? 'bg-[#C8553D] border-[#C8553D] text-white' : 'bg-white border-[#F4F1DE] text-[#2B2D42] hover:border-[#E07A5F]'
                    }`}
                  >
                    {r}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-wrap gap-2 mt-1">
              {(user.relationship_intents || []).map((r) => (
                <span key={r} className="text-xs px-3 py-1 bg-[#FFE5DD] text-[#C8553D] rounded-full font-semibold">
                  {r}
                </span>
              ))}
              {(!user.relationship_intents || user.relationship_intents.length === 0) && (
                <span className="text-sm text-[#B5B5B5]">—</span>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Status */}
      <section className="bg-white border border-[#F4F1DE] rounded-2xl p-5 mb-5">
        <h2 className="text-sm font-bold uppercase tracking-wider text-[#6D6875] mb-3">Account</h2>
        <ul className="space-y-2 text-sm">
          <li className="flex justify-between">
            <span className="text-[#6D6875]">Email</span>
            <span className="font-semibold text-[#2B2D42]">{user.email}</span>
          </li>
          <li className="flex justify-between items-center">
            <span className="text-[#6D6875]">Email verified</span>
            <span className="font-semibold text-[#2A9D8F] flex items-center gap-1">
              {(user.email_verified || user.badges?.email_verified) ? <><ShieldCheck className="w-4 h-4" /> Verified</> : 'Pending'}
            </span>
          </li>
          <li className="flex justify-between items-center">
            <span className="text-[#6D6875]">Premium</span>
            {user.is_premium ? (
              <span className="inline-flex items-center gap-1 font-semibold text-[#E9C46A]" data-testid="profile-premium-active">
                👑 Active
              </span>
            ) : (
              <a href="/app/premium" data-testid="profile-premium-upgrade" className="inline-flex items-center gap-1 text-xs font-bold text-white bg-gradient-to-r from-[#E07A5F] to-[#C8553D] px-3 py-1 rounded-full hover:scale-105 transition-transform">
                👑 Upgrade
              </a>
            )}
          </li>
        </ul>
      </section>

      {/* Quick links */}
      <section className="bg-white border border-[#F4F1DE] rounded-2xl p-5 mb-5" data-testid="profile-quick-links">
        <h2 className="text-sm font-bold uppercase tracking-wider text-[#6D6875] mb-3">My Activity</h2>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
          <a href="/app/saved" data-testid="profile-link-saved" className="flex flex-col items-center gap-2 p-3 bg-[#FFFCF9] hover:bg-[#FFE5DD]/60 border border-[#F4F1DE] rounded-xl text-xs font-bold text-[#2B2D42]">
            <span className="text-2xl">🔖</span>
            Saved
          </a>
          <a href="/app/submit" data-testid="profile-link-submit" className="flex flex-col items-center gap-2 p-3 bg-[#FFFCF9] hover:bg-[#FFE5DD]/60 border border-[#F4F1DE] rounded-xl text-xs font-bold text-[#2B2D42]">
            <span className="text-2xl">➕</span>
            Submit
          </a>
          <a href="/app/my-submissions" data-testid="profile-link-my-submissions" className="flex flex-col items-center gap-2 p-3 bg-[#FFFCF9] hover:bg-[#FFE5DD]/60 border border-[#F4F1DE] rounded-xl text-xs font-bold text-[#2B2D42]">
            <span className="text-2xl">📋</span>
            My Listings
          </a>
          <a href="/app/preferences" data-testid="profile-link-preferences" className="flex flex-col items-center gap-2 p-3 bg-[#FFFCF9] hover:bg-[#FFE5DD]/60 border border-[#F4F1DE] rounded-xl text-xs font-bold text-[#2B2D42]">
            <span className="text-2xl">🎯</span>
            Preferences
          </a>
          <a href="/app/settings" data-testid="profile-link-settings" className="flex flex-col items-center gap-2 p-3 bg-[#FFFCF9] hover:bg-[#FFE5DD]/60 border border-[#F4F1DE] rounded-xl text-xs font-bold text-[#2B2D42]">
            <span className="text-2xl">⚙️</span>
            Settings
          </a>
          <a href="/app/premium" data-testid="profile-link-premium" className="flex flex-col items-center gap-2 p-3 bg-gradient-to-br from-[#E9C46A]/40 to-[#E07A5F]/20 hover:from-[#E9C46A]/60 border-2 border-[#E9C46A] rounded-xl text-xs font-bold text-[#2B2D42]">
            <span className="text-2xl">👑</span>
            Premium
          </a>
        </div>
      </section>

      <p className="text-center text-xs text-[#B5B5B5]" data-testid="profile-footer">
        AfroConnect · Phase 1 — Dating Core
      </p>
    </div>
  );
}
